import { useCallback, useState } from 'react';

type Validator<T, K extends keyof T> = (value: T[K], values: T) => string | null;
type Validators<T> = Partial<{ [K in keyof T]: Validator<T, K> }>;

export function useFormValidation<T>(validators: Validators<T>) {
  const [errors, setErrors] = useState<Partial<Record<keyof T, string>>>({});

  const validateField = useCallback(
    (field: keyof T, value: T[keyof T], values: T) => {
      const validator = validators[field];
      if (!validator) return true;

      const message = validator(value, values);
      setErrors((prev) => ({ ...prev, [field]: message || '' }));
      return !message;
    },
    [validators]
  );

  const validateAll = useCallback(
    (values: T) => {
      let isValid = true;
      const nextErrors: Partial<Record<keyof T, string>> = {};

      (Object.keys(validators) as Array<keyof T>).forEach((field) => {
        const validator = validators[field];
        if (!validator) return;
        const message = validator(values[field], values);
        nextErrors[field] = message || '';
        if (message) isValid = false;
      });

      setErrors((prev) => ({ ...prev, ...nextErrors }));
      return isValid;
    },
    [validators]
  );

  const clearErrors = useCallback(() => {
    setErrors({});
  }, []);

  return { errors, validateField, validateAll, clearErrors };
}
